import {createContext, useContext, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router";
import Box from "@mui/material/Box";
import {Button, CircularProgress, Grid, LinearProgress, Paper, Skeleton, Tooltip, Typography} from "@mui/material";
import ArchiveIcon from "@mui/icons-material/Archive";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import FlightLandIcon from "@mui/icons-material/FlightLand";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import ReplayIcon from "@mui/icons-material/Replay";
import RefreshIcon from "@mui/icons-material/Refresh";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {ButtonColorGradient} from "../../theme/theme";
import {useJobContext} from "../../../views/JobContextProvider/JobContextProvider";
import {useAuth} from "../../../views/AuthProvider/AuthProvider";
import {
    buildMediaMetrics,
    ALL_JOBS_SCOPE_NOTE,
    selectedJobsScopeNote,
    CATALOGUED_FILTER,
    DAMAGED_FILTER,
    IN_LIBRARY_FILTER,
    isAllClientsScope,
    mediaFilterHintText,
    mediaMetricsDataRetriever,
    NO_ONBOARDED_JOBS_NOTE,
    PHYSICAL_DESTROYED_FILTER,
    READ_FAILED_FILTER,
    READING_FILTER,
    RETURNED_FILTER,
    STORED_FILTER,
    VIRTUAL_DESTROYED_FILTER,
    VIRTUALIZED_FILTER,
} from "./mediaMetricsDataRetrieving";
import {
    DATA_MANAGEMENT_PATH,
    dataManagementFilterSearch,
    findOrderUuidByJobId,
    jobPagePath,
    mediaFilterSearch,
} from "./mediaFilterNavigation";
import {fetchTapeDataForReport} from "../../../utils/dashboardCalls";
import {buildMediaReportSheet, buildMetricsSummarySheet, mediaReportFileName} from "../../../utils/reportExport";
import {downloadWorkbook} from "../../../utils/excelWriter";
import {AppSnackbar} from "../../../utils/uiUtils";

/**
 * The shape the tiles are drawn from before any count has arrived, and if they fail to arrive.
 *
 * Zeros, because the tiles need every field a real answer has in order to lay themselves out. They are
 * never shown as zeros on a first load — `MetricsLoadingContext` puts a skeleton over every figure
 * derived from this — but they are shown if the counts fail, where a zero is the honest answer to
 * "we asked and got nothing" and the snackbar says which it was.
 */
const emptyMediaMetrics = buildMediaMetrics({total: 0, audited: 0, virtualized: 0, inProgress: 0, readFailed: 0});

/**
 * Whether the tiles should draw skeletons in place of their figures.
 *
 * A context rather than a `loading` prop threaded through fourteen tiles. The answer is the same for
 * every tile on the card, and a prop that has to be remembered fourteen times is one that will be
 * forgotten once — a single tile left showing a confident `0` beside thirteen skeletons is worse than
 * no skeletons at all, because that `0` reads as a figure that has arrived.
 *
 * Defaults to false so that a tile rendered outside the card, as the tests render them, is a tile with
 * its number in it and needs no provider.
 */
const MetricsLoadingContext = createContext(false);

/**
 * A figure that has not arrived yet.
 *
 * Sized in `em` so it tracks whatever font size the tile it lands in uses, and given an explicit dark
 * surface because MUI derives Skeleton's colour from the palette, which is light here — the default is
 * very nearly invisible on the near-black tiles.
 *
 * `aria-hidden` with a `role="status"` sibling would be the thorough treatment; the card already
 * announces itself as Loading in its header, so this is decoration and says so.
 */
const FigureSkeleton = ({width = '2.5em', height = '1.2em'}) => (
    <Skeleton
        variant="rounded"
        aria-hidden="true"
        data-testid="metric-skeleton"
        sx={{
            width,
            height,
            bgcolor: 'rgba(255, 255, 255, 0.13)',
            borderRadius: 1,
        }}
    />
);

/**
 * How often the counts are re-read while the dashboard is on screen.
 *
 * The card asserts that it is live — a green Live Status dot in the header and a pulsing one on the
 * Progress band — so it has to actually be live. Without this the figures were fixed at whatever was
 * true when the page mounted, which for an operator watching tapes move through a read is the wrong
 * number for the rest of the morning.
 *
 * A minute is the shortest interval that claim needs, and the ceiling is what ArkBridge is being asked
 * to do: every refresh is ten `man_item_count` calls, each a SQL COUNT over vwItemsAPI, and an admin
 * with no client selected counts across every company. Ten queries a minute per open dashboard is
 * comfortably inside that; ten a second would not be. One constant, so it is one line to retune.
 */
export const REFRESH_INTERVAL_MS = 60 * 1000;


const YELLOW = '#FFC540';
/*
 * The exception tiles' colours, as a set — text, surface, border — because contrast is a relationship
 * and changing one without the others is what made these two tiles hard to read in the first place.
 *
 * The design's #EE3F3F on an 8%-red wash over the near-black card came out around 3:1: a dull maroon
 * smudge, and a warning colour nobody notices is not doing its job. The text is lifted to a brighter red
 * and the surface dropped to a dark maroon, which lands at roughly 5.5:1 — comfortably readable at the
 * 12px label size, not just on the big number.
 *
 * Stated opaque rather than as a wash, for the same reason TILE_BG_SOLID below is: a translucent tint
 * takes its colour from whatever it is placed over, so the Exceptions strip would drift if it were ever
 * moved onto the progress band. Recompute both if the card background changes.
 *
 * Still red rather than yellow: every other figure on the card is yellow, so red is what is left to say
 * that this one is a problem, and these are the only tiles reporting something wrong.
 */
const RED = '#FF8A8A';
const ERROR_TILE_BG = '#4A2A2C';
const ERROR_TILE_BORDER = 'rgba(255, 138, 138, 0.55)';
const TILE_BG = 'rgba(0, 0, 0, 0.20)';
const BORDER = '#4a5058';
const LABEL_COLOR = 'rgba(255, 255, 255, 0.60)';
const GREEN = '#38AA58';

/**
 * The label colour both buttons in the title row wear.
 *
 * Refresh wears the same amber ButtonColorGradient as Export Report beside it, as asked for: the portal's
 * buttons are amber everywhere else, and a green one here was the card inventing a colour of its own. A
 * pale green was tried first — same family as the Live Status dot, on the reasoning that the dot claims the
 * figures are current and this button is what makes them current — but consistency with the rest of the
 * portal beats a hue that only means something inside this one card.
 *
 * Near-black on amber rather than white: dark on this gradient is past 8:1, white on it under 3.
 */
const BUTTON_LABEL_COLOUR = '#3A3E3F';

/**
 * The progress band the three virtualization tiles sit on.
 *
 * Lifted very slightly off the card rather than tinted, so the dark tiles inside it read as the same
 * black as the tiles in the headline row above. This is the surface the old single-row progress box
 * had, kept so the section still groups as one thing.
 */
const PROGRESS_BAND_BG = 'rgba(255, 255, 255, 0.04)';

/**
 * Opaque equivalent of TILE_BG once composited over the card background — 0.8 x #3A3E3F.
 *
 * TILE_BG is a 20%-black wash, so it takes its hue from whatever sits behind it. A tile on the
 * progress band would therefore pick up the band's lift and read lighter than the tiles placed
 * directly on the card. Stating the resulting colour outright makes the two rows match exactly,
 * which is the point: the progress tiles should look like the Audit/Catalogued tiles, not like
 * tiles-on-a-band. Recompute this if the card background ever changes.
 */
const TILE_BG_SOLID = '#2E3232';

/**
 * Vertical density of the whole card, gathered here because it is one budget rather than a set of
 * independent choices.
 *
 * The card now occupies the top-left QUARTER of the dashboard — Ingested Data sits beside it and two more
 * graphs below — so its height is a hard constraint rather than a consequence: everything here has to
 * land inside roughly half the viewport at half the page width, with no scrolling. Sizes were pulled in
 * from the original comfortable spacing (padding 2, values at 34px) until it did, keeping the visual
 * hierarchy intact — the headline row is still the largest type, the progress band a step down from it,
 * the exception strip the smallest — just compressed.
 *
 * Change one of these and the card grows or shrinks as a whole, which is the point. The other half of the
 * budget is home.jsx, where the page is pinned to the viewport and the graph row takes what is left; grow
 * this card and the graphs shrink to pay for it.
 */
const CARD_PAD = 1.25;         // Paper inset, was 2.
const CARD_HEADER_PAD = 0.75;  // Below a card title, was 2.
const SECTION_GAP = 1;         // Between the three sections of Inventory & Processing, was 2.
const GRID_GAP = 1;            // Between tiles, was 2.

/**
 * The height of a single-line tile — the Exceptions strip and the two inside Destruction.
 *
 * Stated rather than left to the content, because these two sets of tiles are the same object in two
 * places and were coming out different sizes: the Exceptions strip is as tall as a 12px label and an 18px
 * number make it, while Destruction's two divide up whatever height the Shipment card beside them sets, and
 * that landed a few pixels taller. Nothing about either number was a decision, and they had no reason to
 * agree.
 *
 * 40px is what Destruction's tiles already worked out to, so this is the size the reader is used to and it
 * grows the Exceptions strip by the four pixels of difference — the whole of what this costs the card's
 * height budget. Set as a minimum, so a label that ever wraps makes its tile taller instead of clipping.
 *
 * In px, unlike every other constant here, and not as the 5 spacing units it is worth. `sx` only multiplies
 * by the spacing unit for the properties in its spacing group — margin, padding, gap — and reads a bare
 * number as pixels everywhere else, so `minHeight: 5` is five pixels and silently does nothing at all.
 */
const COMPACT_TILE_H = '40px';

/**
 * How much wider than a third of the row the Destruction card has to be for the tiles inside it to end on
 * the same vertical line as the first tile of the full-width card above.
 *
 * The two do not line up on their own, and the reason is that a tile in the top card is a third of one
 * card's inside, while the Destruction card is a third of the whole row and then pays its own inset again.
 * Writing `i` for a card's inset (padding plus border) and `g` for the gutter, the top card's first tile
 * ends at `(i - g) + (W - 2i + g) / 3` and Destruction's tiles end at `(W + g) / 3 - g - i`; subtract and
 * both `W` and `g` cancel, leaving 4i/3 — about 15px at the current padding, and the sliver of Destruction
 * showing to the left of the tiles above it.
 *
 * Given to the card rather than taken off the tiles above, because three equal thirds that stop on this
 * line would leave 45px of nothing down the right-hand side of the card they sit in. What it costs instead
 * is the seam between Destruction and Shipment, which moves right by the same 15px and so no longer falls
 * inside the gutter above it — a card border a few pixels out of place against a tile edge readers were
 * already tracking down the page.
 */
const CARD_INSET_PX = CARD_PAD * 8 + 1;
const CARD_ALIGN_PX = (4 / 3) * CARD_INSET_PX;

/** Widths for the pair below, stated as one third and two thirds shifted by the amount above. */
const THIRD_CARD = {
    flexBasis: `calc(33.333333% + ${CARD_ALIGN_PX}px)`,
    maxWidth: `calc(33.333333% + ${CARD_ALIGN_PX}px)`,
};
const TWO_THIRDS_CARD = {
    flexBasis: `calc(66.666667% - ${CARD_ALIGN_PX}px)`,
    maxWidth: `calc(66.666667% - ${CARD_ALIGN_PX}px)`,
};

const formatCount = (value) => new Intl.NumberFormat('en-US').format(value ?? 0);

/**
 * Dot that pulses so a header reads as live rather than as a static badge.
 *
 * The expanding glow is drawn with `currentColor`, not a literal rgba, so one set of keyframes serves
 * every colour. Writing the colour into the keyframes instead would mean a second global
 * `@keyframes mediaMetricsLivePulse` block, where whichever loaded last silently wins for both dots.
 *
 * Rendered as a span so it is valid inside the inline label it sits in.
 *
 * @param {string} [color] - Dot and glow colour: green marks the card's live status, yellow the
 *        virtualization progress band.
 * @param {number} [size] - Diameter in px.
 */
const LiveDot = ({color = GREEN, size = 8}) => (
    <Box
        component="span"
        sx={{
            display: 'inline-block',
            width: size,
            height: size,
            // Fixed size inside a flex row, so the label text cannot squash it.
            flexShrink: 0,
            borderRadius: '50%',
            color,
            backgroundColor: color,
            animation: 'mediaMetricsLivePulse 1.8s ease-in-out infinite',
            '@keyframes mediaMetricsLivePulse': {
                '0%': {opacity: 1, boxShadow: '0 0 0 0 currentColor'},
                '70%': {opacity: 0.55, boxShadow: '0 0 0 6px transparent'},
                '100%': {opacity: 1, boxShadow: '0 0 0 0 transparent'},
            },
            '@media (prefers-reduced-motion: reduce)': {animation: 'none'},
        }}
    />
);

/** Small help affordance rendered next to a tile label. */
const InfoHint = ({title}) => (
    <Tooltip title={title}>
        <HelpOutlineIcon sx={{fontSize: 14, color: LABEL_COLOR, ml: 0.5, verticalAlign: 'middle'}}/>
    </Tooltip>
);

/**
 * A titled card matching the dashboard widget surface (same background / border / radius
 * as the widgets in WidgetsGrid so the two sections read as one system).
 */
const MetricsCard = ({icon: Icon, title, action, children}) => (
    <Paper
        elevation={3}
        sx={{
            borderRadius: 2,
            border: `1px solid ${BORDER}`,
            background: '#3A3E3F',
            padding: CARD_PAD,
            height: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
        }}
    >
        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: CARD_HEADER_PAD}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <Icon sx={{color: LABEL_COLOR, fontSize: 18}}/>
                <Typography sx={{fontFamily: 'Poppins', fontSize: '16px', fontWeight: 700, color: '#FFFFFF'}}>
                    {title}
                </Typography>
            </Box>
            {action}
        </Box>
        {children}
    </Paper>
);

/**
 * Stat tile: uppercase label, large value, and optionally a caption or a progress bar.
 *
 * Every figure is yellow but an exception's, which is red. The tile takes no colour argument, because the
 * colour is not a property of the tile: it says whether the number is something to act on. Half these
 * tiles were white and half yellow with nothing to tell the reader which meant what, and a reader
 * scanning for the number that matters was being asked to read three colours to find two meanings.
 *
 * @param {'default'|'error'} [tone] - 'error' tints the tile for exception counts.
 * @param {'large'|'medium'} [size] - 'medium' is the same tile a shade smaller, used by the
 *        progress row so it reads as a subordinate band rather than a second headline row.
 *        Ignored when `compact` is set, which is a different (single-line) layout entirely.
 * @param {string} [suffix] - Appended to the formatted value, e.g. '%'. Kept separate from `value`
 *        so the number still goes through `formatCount` and stays locale-formatted.
 * @param {string} [background] - Overrides the tile surface. Only needed by tiles that do not sit
 *        directly on the card, where the default translucent wash would pick up what is behind it.
 * @param {function} [onClick] - Makes the whole tile the target, rather than putting a link inside it:
 *        the figure is what the reader is looking at, so the figure is what they should be able to
 *        click. Given one, the tile takes focus, answers Enter and Space, and shows a hover state.
 * @param {string} [clickLabel] - Accessible name for a clickable tile. Worth setting, because the
 *        default reads out the label and the number, which does not say where the click goes.
 */
const StatTile = ({label, value, caption, progress, hint, tone = 'default', compact = false, size = 'large', suffix, background = TILE_BG, onClick, clickLabel}) => {
    const isError = tone === 'error';
    const isMedium = !compact && size === 'medium';
    const loading = useContext(MetricsLoadingContext);
    /*
     * Not clickable while the figure is still coming.
     *
     * The click is honest either way — it navigates to a filtered table, which does not depend on the
     * count — but the affordance is not: a tile that offers a hover border and an accessible name reading
     * "AUDIT/CATALOGUED: 0" is claiming to have an answer it has not got. It becomes clickable the moment
     * the number lands, which is well under a second once the API stops serialising.
     */
    const clickable = typeof onClick === 'function' && !loading;

    /*
     * A div with role="button" rather than a real <button> or an <a>.
     *
     * The tile's contents are Typography — <p> elements — and its label carries a tooltip icon. Both are
     * invalid inside a button, and browsers recover from invalid nesting by moving nodes, which loses the
     * layout rather than merely warning about it. role plus tabIndex plus a keydown handler gives a
     * screen reader and a keyboard the same tile a mouse gets, which is what the affordance owes them.
     *
     * The cost, stated because it is a real one: no middle-click and no ctrl-click to open the filtered
     * table in a new tab, which an anchor would give for free. The click navigates within the app to a
     * URL that carries the filter in its query string, so the destination is at least linkable and
     * reloadable once you are there.
     */
    const interaction = clickable ? {
        role: 'button',
        tabIndex: 0,
        'aria-label': clickLabel ?? `${label}: ${formatCount(value)}`,
        onClick,
        onKeyDown: (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            // Space scrolls the page by default, which is the one thing a button must not do.
            event.preventDefault();
            onClick(event);
        },
    } : {};

    return (
        <Box
            {...interaction}
            /*
             * Named after its own label, so a test can hold the tile itself rather than the text inside it.
             * Wanted because the box is what carries the height these tiles are supposed to share, and the
             * only route to it from the label is the DOM parent — which is exactly what a test should not
             * be walking.
             */
            data-testid={`tile-${label}`}
            sx={{
                borderRadius: 2,
                border: `1px solid ${isError ? ERROR_TILE_BORDER : BORDER}`,
                background: isError ? ERROR_TILE_BG : background,
                padding: compact ? 0.75 : 1,
                /*
                 * One height for every single-line tile, wherever it is on the card — see the constant. It is
                 * the height, not a floor to fill from, which is why the compact tile stops claiming 100% of
                 * whatever it was put in: filling was what made Destruction's two disagree with the
                 * Exceptions strip in the first place, and inside a stretched stack it would now claim the
                 * whole stack each.
                 */
                height: compact ? 'auto' : '100%',
                minHeight: compact ? COMPACT_TILE_H : undefined,
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: compact ? 'row' : 'column',
                alignItems: compact ? 'center' : 'stretch',
                justifyContent: compact ? 'space-between' : 'flex-start',
                gap: compact ? 1 : 0,
                ...(clickable ? {
                    cursor: 'pointer',
                    // Border and lift only — the surface colour is doing other work on this card (the
                    // exception tiles tint by tone, the progress band tiles are pinned opaque), so
                    // hovering must not repaint it or a clickable exception tile would lose its warning.
                    transition: 'border-color 120ms ease, box-shadow 120ms ease',
                    '&:hover': {borderColor: YELLOW, boxShadow: `0 0 0 1px ${YELLOW}`},
                    // Keyboard focus has to be visible without being on screen for every mouse click.
                    '&:focus-visible': {outline: `2px solid ${YELLOW}`, outlineOffset: 2},
                    '@media (prefers-reduced-motion: reduce)': {transition: 'none'},
                } : {}),
            }}
        >
            <Typography
                sx={{
                    fontFamily: 'Poppins',
                    fontSize: '12px',
                    fontWeight: 500,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: isError ? RED : LABEL_COLOR,
                }}
            >
                {label}
                {hint ? <InfoHint title={hint}/> : null}
            </Typography>

            <Typography
                sx={{
                    fontFamily: 'Poppins',
                    fontSize: compact ? '18px' : isMedium ? '20px' : '24px',
                    fontWeight: 700,
                    lineHeight: 1.2,
                    color: isError ? RED : YELLOW,
                    mt: compact ? 0 : 0.25,
                }}
            >
                {loading ? <FigureSkeleton/> : <>{formatCount(value)}{suffix ?? ''}</>}
            </Typography>

            {/*
              * The caption and the bar keep their space while loading rather than being dropped.
              *
              * Both are derived from the placeholder zeros, so their text would be a lie — 'No media has a
              * library location yet' is a statement about an account, not about a request in flight. But
              * removing the elements would shrink the tile and then grow it again when the counts land, and
              * this card is height-budgeted to the pixel against the graphs beside it (see CARD_PAD). So the
              * space is held and the content is a skeleton: the layout the reader gets is the layout they keep.
              */}
            {caption ? (
                <Typography sx={{fontFamily: 'Poppins', fontSize: '11px', color: LABEL_COLOR, mt: 0.25}}>
                    {loading ? <FigureSkeleton width="8em" height="1em"/> : caption}
                </Typography>
            ) : null}

            {progress !== undefined ? (
                loading ? (
                    <Box sx={{mt: 0.75}}><FigureSkeleton width="100%" height="4px"/></Box>
                ) : <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{
                        mt: 0.75,
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.12)',
                        '& .MuiLinearProgress-bar': {backgroundColor: YELLOW, borderRadius: 2},
                    }}
                />
            ) : null}
        </Box>
    );
};

/*
 * There was a SelectClientPrompt here — a panel shown to an admin who had picked no client, in place of
 * every tile, telling them to pick one. It is gone: the header selector already read 'All Jobs', and a
 * card that answers a scope of "all of them" with a request to choose one is the portal contradicting
 * itself. The figures are shown at that scope, and the reconciliation caveat the panel stood for is
 * answered rather than captioned — the counts are scoped to the jobs the selector lists, so they add up to
 * the per-job badges beside them. See isAllClientsScope in mediaMetricsDataRetrieving.js and `scopeJobIds`.
 */

/**
 * Icon-above-value tile used inside the Shipment card.
 *
 * Clickable on the same terms as {@link StatTile} and by the same means, rather than by sharing code with
 * it: the two differ in layout, not in what a click is, and duplicating five lines of ARIA is cheaper than
 * a third layout mode on a component that already has two. Kept identical deliberately — a reader who has
 * learned that a tile is a control on one card must not find out it is only text on another.
 *
 * @param {function} [onClick] - Makes the whole tile the target. Given one, the tile takes focus, answers
 *        Enter and Space, and shows a hover state.
 * @param {string} [clickLabel] - Accessible name, since the default would read out a number and a word
 *        without saying where the click goes.
 */
const ShipmentTile = ({icon: Icon, value, caption, progress, onClick, clickLabel}) => {
    const loading = useContext(MetricsLoadingContext);
    // Not clickable while the figure is still coming, for the reason given in StatTile.
    const clickable = typeof onClick === 'function' && !loading;
    const interaction = clickable ? {
        role: 'button',
        tabIndex: 0,
        'aria-label': clickLabel ?? `${caption}: ${formatCount(value)}`,
        onClick,
        onKeyDown: (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            onClick(event);
        },
    } : {};

    return (
    <Box
        {...interaction}
        sx={{
            borderRadius: 2,
            border: `1px solid ${BORDER}`,
            background: TILE_BG,
            padding: 1,
            height: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            ...(clickable ? {
                cursor: 'pointer',
                transition: 'border-color 120ms ease, box-shadow 120ms ease',
                '&:hover': {borderColor: YELLOW, boxShadow: `0 0 0 1px ${YELLOW}`},
                '&:focus-visible': {outline: `2px solid ${YELLOW}`, outlineOffset: 2},
                '@media (prefers-reduced-motion: reduce)': {transition: 'none'},
            } : {}),
        }}
    >
        <Icon sx={{color: YELLOW, fontSize: 18}}/>
        <Typography sx={{fontFamily: 'Poppins', fontSize: '20px', fontWeight: 700, color: YELLOW, lineHeight: 1.2, mt: 0.25}}>
            {loading ? <FigureSkeleton/> : formatCount(value)}
        </Typography>
        <Typography
            sx={{
                fontFamily: 'Poppins',
                fontSize: '10px',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: LABEL_COLOR,
                mt: 0.25,
            }}
        >
            {caption}
        </Typography>
        {/* The caption above is a fixed label rather than a figure, so it stays put while loading. */}
        {progress !== undefined ? (
            loading ? (
                <Box sx={{width: '100%', mt: 0.75}}><FigureSkeleton width="100%" height="4px"/></Box>
            ) : <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                    width: '100%',
                    mt: 0.75,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                    '& .MuiLinearProgress-bar': {backgroundColor: YELLOW, borderRadius: 2},
                }}
            />
        ) : null}
    </Box>
    );
};

/**
 * MediaMetrics renders the dashboard summary section (inventory & processing, destruction and
 * shipment counters) shown above the graph widgets.
 *
 * Counts come from `man_item_count`, scoped by the dashboard job selector exactly like the graph
 * widgets below it: selecting a customer/job narrows every tile to that job, and an admin with
 * nothing selected sees the combined total across every company.
 *
 * @param {Object} [data] - Optional pre-supplied metrics (same shape as the retriever builds);
 *                          when given, no fetch happens. Used by tests and Storybook.
 * @param {number} [cid] - Overrides the selected customer scope; omit to follow the selector.
 * @param {number|number[]} [jobIds] - Overrides the selected job scope; omit to follow the selector.
 *        Job Management shows this card for the one job being managed, which is not necessarily the job
 *        the header selector is pointing at — the two are independent, and a card that quietly reported
 *        a different job's tapes than the table underneath it would be worse than no card.
 * @param {number} [refreshIntervalMs] - How often to re-read the counts; defaults to REFRESH_INTERVAL_MS.
 *        A shorter interval belongs to a page, not to the card: on the processing tab the counters sit
 *        above the tape table for one job and both refresh together, so they have to keep the table's
 *        pace or the two will disagree for most of a minute. The dashboard, which shows every job of
 *        every company, keeps the slower default — see REFRESH_INTERVAL_MS for what sets the ceiling.
 * @param {string} [orderUuid] - The Nexus order uuid of the job in scope, when the mounting page already
 *        knows it. Saves a clicked tile the lookup it would otherwise need: the dashboard's job selector
 *        carries only the ArkBridge job id, but the job pages are routed by order uuid, and the page
 *        showing this card is on one of those routes already.
 * @param {function} [onSelectFilter] - Called with a MEDIA_FILTERS descriptor instead of navigating, for a
 *        page that already shows the tape table this card's tiles describe. The processing tab is that page:
 *        the table is a few hundred pixels below the tile, so sending the reader on a round trip through the
 *        router to arrive back where they are standing would scroll the page and re-mount the table for no
 *        gain. Only the dashboard, which has no table, needs the link.
 *
 *        It applies to the tiles about one job's processing run — In Library and Reading — and not to
 *        Audit/Catalogued or Physical Stored, which describe the account's inventory and open Data Management
 *        from every page. See openTapeLibrary; those two fall back to this handler only for a reader the
 *        Data Management route would answer NotFound to.
 * @constructor
 */
export const MediaMetrics = ({data: externalData, cid, jobIds, refreshIntervalMs = REFRESH_INTERVAL_MS, orderUuid, onSelectFilter}) => {
    const [fetchedData, setFetchedData] = useState(null);
    /*
     * Whether the figures on the tiles are figures at all.
     *
     * A first load that fails resolves to null, and null renders as `emptyMediaMetrics` — thirteen zeros, under
     * a header still claiming Live Status. Zeros are exactly what a genuinely empty account looks like, so
     * there was nothing on the card to tell a reader which of the two they were looking at, and the honest one
     * is the rarer one. Held separately from the data rather than encoded in it so the tiles keep taking plain
     * numbers; all it changes is what the header says.
     */
    const [countsUnavailable, setCountsUnavailable] = useState(false);
    const [loading, setLoading] = useState(externalData === undefined);
    const [exporting, setExporting] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [openingMediaList, setOpeningMediaList] = useState(false);
    const [snackbar, setSnackbar] = useState({open: false, message: '', severity: 'info'});
    /*
     * The figures last put on screen, as a string that can be compared.
     *
     * Needed because a refresh hands back a newly built object every time, so state was being replaced on
     * every tick whether or not one figure had moved — a re-render of the whole card, and with it a restart
     * of every progress bar's CSS transition, twice a minute on the processing tab. A card that twitches for
     * no reason reads as a page with a fault, which is exactly what was reported.
     *
     * Compared by value rather than by identity, since identity is what was always different and never the
     * question. The shape is plain numbers and strings assembled in a fixed key order by buildMediaMetrics,
     * so its JSON is a faithful signature of what the tiles will draw: nothing the card renders is outside
     * it, and nothing in it is unrendered. A signature and not a deep compare because it is one string
     * comparison a tick, against a ref that costs a render nothing.
     *
     * A ref and not state on purpose — writing it must not itself be a reason to render.
     */
    const shownCounts = useRef(null);
    /*
     * The poll's own read, held where the Refresh button can reach it.
     *
     * A ref rather than a second copy of the fetch, so the button and the timer cannot drift apart: the
     * click runs the identical closure the tick does, against the identical scope, with the identical
     * rules about what a failure is allowed to do to the screen. A copy would be two things to keep
     * right, and the interesting behaviour here is all in those rules.
     *
     * A ref rather than state because it holds a function built inside the effect, and writing it must
     * not itself be a render — same reasoning as shownCounts above. It is null exactly when there is
     * nothing to refresh: no client picked, or the card being fed its figures by a parent.
     */
    const refreshNow = useRef(null);
    // `setSelection` is written to only by openTapeLibrary, which has to point the header selector at the job
    // it is about to open a list for — Data Management is scoped by that selector and by nothing else.
    // `allJobIds`/`jobGroupsPending` are defaulted rather than assumed, because harnesses and pages that
    // mock this context supply only the selection fields — and a missing job list must degrade to "no
    // aggregate to take", not to a TypeError on a dashboard.
    const {selectedJobIds, selectedCid, setSelection, allJobIds = [], jobGroups = [], jobGroupsPending = false} = useJobContext();
    const {isAdmin, isFeatureAvailable} = useAuth();
    const navigate = useNavigate();
    // Normalised to an array here rather than at the call sites, so a caller with a single id can pass
    // it as one: the API layer accepts either, but jobIdsKey below needs something it can join.
    const effectiveJobIds = jobIds === undefined ? selectedJobIds : (Array.isArray(jobIds) ? jobIds : [jobIds]);
    const effectiveCid = cid ?? selectedCid;
    /*
     * An admin who has selected nothing is reporting on more than one client, and is told so.
     *
     * This is fetched rather than refused — the header says 'All Jobs', and a card that answers that with a
     * request to choose one client is the portal contradicting itself. What it took two attempts to get
     * right is *which* figure answers it; see `scopeJobIds` immediately below.
     *
     * `effectiveCid` stays undefined at this scope rather than being pinned to 0: the API layer derives
     * it (dashboardCalls._deriveCID → 0 for an admin), which is the one place that mapping lives.
     */
    const allClientsScope = externalData === undefined && isAllClientsScope(isAdmin, effectiveCid);
    /*
     * A selection that spans clients, which is a scope of its own — not 'all jobs' and not one client.
     *
     * The selector takes any number of jobs under any number of companies (isMulti in JobContextProvider), so
     * an admin can tick two jobs at one client and one at another. There is no single `cid` to send for that,
     * which is what puts it inside `allClientsScope` above — but it is nothing like the unbounded case:
     * the reader has named the jobs, so the figures are bounded by exactly those ids.
     *
     * Distinguished because three things read differently once it exists: the caption must not claim to cover
     * every onboarded job, the card need not wait for the selector's full list (the scope is already known),
     * and Export is worth offering, since the objection to it at 'All Jobs' was a spreadsheet of every tape
     * of every client in one click, which a ticked handful of jobs is not.
     */
    const multiClientSelection = allClientsScope && effectiveJobIds.length > 0;
    /*
     * What the counts are actually bounded by — the selection, or every job the portal has onboarded.
     *
     * With nothing selected there is no cid and no job id to send, and a count with neither is a count of
     * every company in `backupsaas_settings`: ArkBridge holds media for companies Nexus has never been told
     * about and media that was never onboarded as an order, so that total came out three orders of
     * magnitude above the sum of the jobs on the screen below it. 'All Jobs' means the jobs in the
     * selector, so those are the ids that go with the request, and the total is then the sum of the
     * per-job figures the same selector shows — which is what was asked for.
     *
     * One request per tile either way, not one per client: the fulfiller applies `vi.JobID in (…)`
     * independently of the company, and a JobId identifies one job of one company.
     *
     * Only substituted when nothing narrower is in scope. A caller that passed `jobIds` explicitly — the
     * processing tab — keeps them even if it passed no cid, because it is reporting on its own job and not
     * on the platform.
     */
    const scopeJobIds = effectiveJobIds.length === 0 ? allJobIds : effectiveJobIds;
    const jobIdsKey = scopeJobIds.join(',');
    /*
     * Whether this card has any scope of its own, or is leaning on the selector's list for one.
     *
     * The substitution above, and the two gates below, all hang on the same condition and are stated once
     * here because getting them out of step is what put a wrong figure on screen: they were written as
     * `allClientsScope`, an admin-only test, so a customer-role reader at 'All Jobs' fell past all three
     * and sent a count with no job id at all. SaaS does not read that as 'every job' — ItemCountFulfiller
     * substitutes the deployment's own configured JobId — so the card reported one arbitrary job.
     *
     * A caller that supplies its own data or its own job ids is excluded, not because of who is reading
     * but because it already knows its scope: the processing tab passes `jobIds`, and the selector's list
     * cannot change what those ids count.
     */
    const usesJobList = externalData === undefined && effectiveJobIds.length === 0;
    /*
     * The list is fetched by the header, so at this scope the card has to wait for it.
     *
     * Without the wait the effect fires its thirteen counts unscoped on mount and thirteen more a moment
     * later when the list lands: twenty-six heavy COUNTs over vwItemsAPI for one page view, and the figure
     * the reader sees is whichever batch answers first. The header mounts before the routed content
     * (App.js), so this is announced before this effect first runs.
     *
     * Safe to wait on because the header resolves it on every path out of the fetch, the nothing-to-fetch
     * case included (see the `setJobGroups(undefined)` contract in JobContextProvider) — a card that waited
     * on a flag that could stay pending would sit under a skeleton for good.
     */
    const waitingForJobList = usesJobList && jobGroupsPending;
    /*
     * Nothing onboarded, so nothing to aggregate — reported as zeros rather than by dropping the job scope.
     *
     * Dropping it would fall back to a count with no job scope, which is the figure this was fixed to stop
     * showing: platform-wide for an admin, and the deployment's injected JobId for a customer. Zeros with
     * the reason said out loud is the answer that matches the selector beside it, which lists no jobs either.
     */
    const nothingOnboarded = usesJobList && !jobGroupsPending && scopeJobIds.length === 0;
    /*
     * The line under the title, or nothing at all when the card is reporting on one client.
     *
     * Nothing while the job list is still coming, too: the scope is not known yet, and a caption naming the
     * wrong one for a second is worse than a title with no subtitle for a second.
     *
     * A selection spanning clients gets a caption of its own, counting the jobs it holds. ALL_JOBS_SCOPE_NOTE
     * would be a false claim there — it says every job onboarded to the portal, and the reader has just
     * narrowed that to three — and 'no caption' would leave two visibly different scopes looking alike.
     */
    const scopeNote = waitingForJobList ? null
        : nothingOnboarded ? NO_ONBOARDED_JOBS_NOTE
            : multiClientSelection ? selectedJobsScopeNote(scopeJobIds.length)
                : allClientsScope ? ALL_JOBS_SCOPE_NOTE
                    : null;
    /*
     * Whether this mounting of the card owns its figures, and so has anything to refresh.
     *
     * Rendered from the same condition the effect returns early on rather than from refreshNow itself,
     * because a ref cannot be rendered from: writing it is not a render, so a button gated on
     * `refreshNow.current` would appear one render late and linger one render after it stopped working.
     * A parent that passes `data` is the source of those figures — a Refresh here could not reach it.
     *
     * Offered at all-jobs scope, unlike before: the counts are real and a minute old like any others, and
     * an admin watching the aggregate has the same reason to re-ask as anyone else. Withheld when there is
     * nothing onboarded to count, where the effect runs no query and a click could only re-read zeros.
     */
    const canRefresh = externalData === undefined && !nothingOnboarded;
    /*
     * Whether there is a report to take, which is now every scope that has media in it.
     *
     * The export used to be withheld at 'All Jobs' on the reasoning that reading a total is not the same as
     * taking the rows behind it away with you. Asked for anyway, and the objection has been answered by the
     * scope work since: the sweep is bounded by the same job ids the tiles were counted with (`scopeJobIds`),
     * so at this scope it is every job the selector lists and not every company ArkBridge holds — the file
     * describes the card that produced it, whichever scope that card is on.
     *
     * Withheld only when there is nothing onboarded to sweep, where the tiles are zeros and the workbook
     * would be two headers and no rows.
     */
    const canExport = !nothingOnboarded;
    /*
     * 'All Jobs' rather than a selection, which the file has to say for itself.
     *
     * Same ids either way — `scopeJobIds` substitutes the full list — so nothing downstream could tell the
     * two apart, and 'every job onboarded, as of this morning' and 'the three I ticked' are not the same
     * statement about the business.
     */
    const exportingAllJobs = allClientsScope && !multiClientSelection;
    /*
     * Job id → the reference the selector shows for it, for the export's job column.
     *
     * Taken from the header's own list rather than from the media rows, so the file names a job the way the
     * screen does: the rows carry ArkBridge's JobDescription, which is not always the order number the
     * reader ticked. The row's own value is still the fallback — see mediaReportJobLabel — for a job that
     * has left the selector since, or media the selector never listed.
     */
    const jobLabelsById = useMemo(() => {
        const labels = {};
        jobGroups.forEach(group => (Array.isArray(group?.items) ? group.items : []).forEach(item => {
            if (item?.key !== undefined && item?.key !== null) labels[item.key] = item.label;
        }));
        return labels;
    }, [jobGroups]);

    useEffect(() => {
        if (externalData !== undefined) {
            setLoading(false);
            // Nothing here owns a fetch, so there is nothing for the button to run — and the closure a
            // previous scope left behind would refresh the wrong client if it were ever reached.
            refreshNow.current = null;
            return;
        }
        /*
         * Both of these leave the card without a fetch, and differ in what the reader is told meanwhile.
         *
         * Waiting deliberately leaves `loading` true: the skeletons stay, which is honest — the figures are
         * coming — and it is the alternative to counting the wrong thing first. Nothing onboarded resolves
         * to zeros and a caption, and clears any counts a previous scope left on screen.
         */
        if (waitingForJobList) return;
        if (nothingOnboarded) {
            shownCounts.current = null;
            setFetchedData(null);
            // Zeros here are the answer, not a missing one — there is nothing to count. Cleared rather than
            // left alone because a previous scope may have failed, and its warning must not carry over.
            setCountsUnavailable(false);
            setLoading(false);
            refreshNow.current = null;
            return;
        }
        const controller = new AbortController();
        /*
         * Every request this card makes is marked `background`, the first load included.
         *
         * `background` keeps them out of the global loading wash the axios interceptor puts over the
         * window while a request is in flight (see index.js). Unmarked, a tick washed the whole page
         * out white for as long as its thirteen counts took to answer — reported as a white flash every
         * ten seconds, and read as the page reloading itself rather than refreshing a number.
         *
         * The first load used to be left unmarked on the grounds that there the reader is waiting and the
         * wash is what tells them so. Both halves of that were wrong. The card says it is loading on its
         * own account — 'Loading' and a spinner in the header, skeletons on every tile face — and says it
         * inside its own borders, where the reader is looking. And the wash is not a hint but a sheet:
         * 100vw by 100vh at z-index 99999 over the entire portal, sidebar and header included, swallowing
         * every click until the last of the thirteen counts had landed. On a slow link that was six
         * seconds in which nobody could open a job, run a search, or leave the page. A reader who does not
         * care about the counts should not have to wait for them.
         */
        const requestOptions = {signal: controller.signal, jobIds: scopeJobIds, background: true};

        /**
         * Puts counts on screen and records what they were, so the next tick can tell what moved.
         *
         * Also the one place the header's claim is settled, because this is the one place a null can reach the
         * screen: the first load commits whatever it is given, a refresh commits only what is not null (see
         * applyRefreshed), so setting the flag from `next` here covers both — a failed first load says so, and
         * the next tick that succeeds takes the words back off the header.
         */
        const commit = (next) => {
            shownCounts.current = next === null ? null : JSON.stringify(next);
            setCountsUnavailable(next === null);
            setFetchedData(next);
        };

        /*
         * The first load asks again before it gives up, and keeps the skeletons while it does.
         *
         * It used to commit whatever it was given, the null a failure produces included, on the grounds that
         * nothing was on screen yet so there was no better answer to keep. The reasoning was sound and the
         * result was not: `data` falls back to emptyMediaMetrics when fetchedData is null, so a single failed
         * request painted thirteen zeros — and zeros are a perfectly ordinary answer for a company with no
         * media, which makes them indistinguishable from counts. They then stayed until the next scheduled
         * tick, a whole refresh interval later.
         *
         * Seen on 28 Sep 2026: one man_item_count answered 401 because the session had expired while the
         * container restarted, and the card read COUNTS UNAVAILABLE over thirteen zeros until a later tick
         * replaced them. Both the ordinary causes are transient and self-clearing — an expired access token
         * that the interceptor is in the middle of refreshing, and a cold SaaS endpoint lookup, which made
         * that same batch take 15 to 18 seconds server-side against 0.8 to 3.5 once warm.
         *
         * So a failure that still has an attempt left commits nothing and swallows the retriever's
         * setLoading(false). The card goes on saying it is loading, which is true — it is about to ask again —
         * and the reader is never shown a number that was not counted. Only the last attempt commits the null,
         * and then the header says so.
         */
        const FIRST_LOAD_ATTEMPTS = 3;
        const FIRST_LOAD_RETRY_MS = 3000;
        let firstLoadTimer = null;

        const firstLoad = (attempt) => {
            const lastAttempt = attempt >= FIRST_LOAD_ATTEMPTS;
            let retrying = false;

            mediaMetricsDataRetriever(
                next => {
                    if (next === null && !lastAttempt) {
                        retrying = true;
                        firstLoadTimer = setTimeout(() => firstLoad(attempt + 1), FIRST_LOAD_RETRY_MS);
                        return;
                    }
                    commit(next);
                },
                // The one `false` that must not get through is the one that follows a failure we are about to
                // ask again about, so that the gap between the attempts still reads as loading. Keyed off
                // `retrying` rather than off the attempt number, which would have swallowed the `false` that
                // follows a perfectly good first answer and left the card in skeletons for good. The
                // retriever sets the data before it clears the flag, so this is always decided by then.
                busy => {
                    if (!busy && retrying) return;
                    setLoading(busy);
                },
                effectiveCid,
                requestOptions,
            );
        };

        firstLoad(1);

        /*
         * Three more deliberate differences from the first load above, beyond the marking of the
         * requests themselves, all so that a refresh can only ever improve what is on screen:
         *
         *   - setLoading is not called, or the header would drop 'Live Status' for 'Loading' and the
         *     dot would become a spinner once a minute, which reads as a fault rather than a refresh.
         *   - a null from a failed request is dropped instead of blanking the card. The retriever
         *     nulls its data on error, which is right for a first load — zeros beat stale nonsense
         *     when nothing has ever been shown — but wrong for a refresh, where one dropped request
         *     would wipe six good tiles to zero. Zeroed tiles are indistinguishable from a genuinely
         *     empty account, so this is the more honest failure: keep the last known counts.
         *   - counts identical to the ones already shown are dropped too, so the tick changes nothing
         *     rather than redrawing the same figures. That is the common case by far: tapes move through a
         *     read every few minutes, and the card asks every half minute on the processing tab, so most
         *     ticks have nothing to report and the reader should not be able to tell that one happened.
         *     There is no cheaper way to know: man_item_count answers with a number, so the only thing
         *     that can detect a change is the request itself. What this removes is the redraw, not the query.
         */
        const applyRefreshed = (next) => {
            if (next === null) return;
            if (JSON.stringify(next) === shownCounts.current) return;
            commit(next);
        };

        const refresh = () => {
            // A tab parked on the dashboard behind other windows is not being read, so its queries
            // buy nothing. The next tick that lands while visible picks the figures back up.
            if (document.hidden) return;
            mediaMetricsDataRetriever(applyRefreshed, () => {}, effectiveCid, requestOptions);
        };
        const refreshTimer = setInterval(refresh, refreshIntervalMs);

        /*
         * The same read on a click, for a reader who will not wait out the rest of the minute.
         *
         * Two deliberate differences from the tick, both because somebody asked for this one:
         *
         *   - the visibility check is gone. It exists to stop a tab nobody is looking at querying
         *     ArkBridge; a click is proof somebody is looking.
         *   - setRefreshing is passed where the tick passes a no-op, so the button can say it is
         *     working. The retriever calls it true on entry and false on both outcomes, which is
         *     exactly the in-flight window and needs nothing else to track it.
         *
         * Still marked `background`, so the axios interceptor does not wash the page white on a click.
         * The spinner in the button is the better answer to 'is it doing something': it is where the
         * reader is already looking, and it leaves the figures they are comparing against readable.
         *
         * A failure is reported here, unlike on a tick. Silence is honest for something nobody asked
         * for, but a button that swallows its own failure reads as a broken button, and the reader has
         * no other way to tell that the counts in front of them are the ones from a minute ago.
         */
        refreshNow.current = () => mediaMetricsDataRetriever(
            next => {
                if (next === null) {
                    setSnackbar({open: true, message: 'Could not refresh the media metrics. The figures shown are the last ones read.', severity: 'warning'});
                    return;
                }
                applyRefreshed(next);
            },
            setRefreshing,
            effectiveCid,
            requestOptions,
        );

        /*
         * Both the timer and the in-flight requests die here. That is what scopes the polling to the
         * page that mounted the card: navigating away unmounts it and runs this cleanup — no
         * route-awareness or global scheduler needed. It also runs when the client or job selection
         * changes, so a stale tick can never resolve against the new scope.
         */
        return () => {
            clearInterval(refreshTimer);
            // A retry still waiting out its delay holds the signal aborted on the next line and the scope this
            // run was built for, so it must not be allowed to fire after the run it belongs to has ended.
            clearTimeout(firstLoadTimer);
            controller.abort();
            // The closure this run installed reads the scope this run was built for and holds the signal
            // just aborted, so it must not outlive the run — the next one installs its own.
            refreshNow.current = null;
            setRefreshing(false);
        };
        // scopeJobIds is a fresh array each render — jobIdsKey is the stable identity.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [externalData, effectiveCid, jobIdsKey, refreshIntervalMs, waitingForJobList, nothingOnboarded]);

    const data = externalData ?? fetchedData ?? emptyMediaMetrics;
    const {inventory, virtualization, exceptions, destruction, shipment} = data;

    const openSnackbar = (message, severity = 'info') => setSnackbar({open: true, message, severity});

    /**
     * Builds the report and hands it to the browser.
     *
     * Two sheets: the counters above, and the media they count. The media sheet is the tape table with
     * its pagination removed — same columns, same formatting, every row rather than the ten on screen,
     * which is the whole reason to export rather than screenshot.
     *
     * Scoped to `effectiveCid`/`scopeJobIds`, the same scope the tiles were counted with, so the file
     * cannot describe a different selection than the card that produced it. On the processing tab that is
     * the one job being managed; on the dashboard it follows the header selector, and with nothing ticked
     * it is every job the selector lists — `scopeJobIds`, not the empty selection, for the same reason the
     * counts use it: no cid and no job id is a sweep of every company in `backupsaas_settings`, which is
     * both a different number from the tiles above the button and a great deal more of ArkBridge's time.
     *
     * The row sweep is the slow part — a large account is minutes of paged querying — so the button
     * disables itself and says what it is doing rather than looking ignored. Failure is a snackbar, not
     * a thrown error: an export that does not happen must not take the dashboard down with it.
     */
    const handleExport = () => {
        if (exporting) return;
        setExporting(true);
        const scope = {cid: effectiveCid, jobIds: scopeJobIds, allJobs: exportingAllJobs};
        fetchTapeDataForReport(scope)
            .then(({rows, count, truncated}) => {
                // The job column earns its place only once there is more than one job in the file; below
                // that every row would repeat what the filename already says. See buildMediaReportSheet.
                const media = buildMediaReportSheet(rows, undefined, scopeJobIds.length > 1 ? {jobLabelsById} : {});
                const summary = buildMetricsSummarySheet(data, count, scopeJobIds.length || undefined);
                return downloadWorkbook([
                    {name: 'Summary', headers: summary.headers, rows: summary.rows},
                    {name: 'Media', headers: media.headers, rows: media.rows, text: true},
                ], mediaReportFileName(scope)).then(() => {
                    // Said plainly rather than left for the reader to notice a round number of rows:
                    // a report that is quietly short is one someone will make a decision on.
                    if (truncated) {
                        openSnackbar(`Exported the first ${rows.length.toLocaleString('en-US')} of ${count.toLocaleString('en-US')} media. Narrow the job selection to export the rest.`, 'warning');
                    } else if (rows.length === 0) {
                        openSnackbar('No media in the current selection, so the report is empty.', 'info');
                    }
                });
            })
            .catch(error => {
                console.error("Error exporting the media report:", error);
                openSnackbar('Could not build the report. Please try again.', 'error');
            })
            .finally(() => setExporting(false));
    };

    /*
     * Whether a job's own page is the destination, which needs exactly one job in scope.
     *
     * On the processing tab there always is one; on the dashboard it is whatever the header selector points
     * at, and the selector enforces at most one. With nothing selected the tiles count every job of every
     * client, and no single job's page can show that — so In Library and Reading fall back to Data
     * Management, which is scoped by the same selector and can. See openJobRunList: the tiles stay
     * clickable at every scope, and only the destination changes with the number of jobs in scope.
     */
    const scopedJobId = effectiveJobIds.length === 1 ? effectiveJobIds[0] : undefined;
    const canOpenMediaList = scopedJobId !== undefined && !allClientsScope;

    /**
     * Opens the job's tape table with the filter that produced a tile's figure.
     *
     * The filter travels in the URL, so the table is reached by a link rather than by a handoff: reload
     * it, bookmark it, paste it to a colleague, and it still shows the same media. See
     * mediaFilterNavigation for the parameters.
     *
     * The order uuid is what the route needs and the dashboard does not have — its selector is built from
     * ArkBridge job ids — so it is looked up unless the mounting page has passed one in. That lookup is
     * a request, hence the guard against a second click while it is in flight, and hence a snackbar
     * rather than a silent no-op when it comes back empty: an ArkBridge job that was never onboarded as
     * a Nexus order has no page to open, and the reader is owed that answer instead of a dead tile.
     *
     * @param {object} filter - A MEDIA_FILTERS descriptor; its `key` is what the table reads back.
     */
    const openMediaList = (filter) => {
        if (openingMediaList || !canOpenMediaList) return;

        // The page already shows the table, so narrow it where it stands. Checked before anything else:
        // there is no uuid to resolve and no route to change, and going through the router would scroll the
        // reader away from the tile they just clicked and re-mount the table under them.
        if (onSelectFilter) {
            onSelectFilter(filter);
            return;
        }

        const search = mediaFilterSearch(filter.key);
        // Optional-called: a caller mounting this card outside AuthProvider's full contract still gets a
        // working tile, and the customer route is the safe default — it is the one with fewer powers.
        const canManageOrders = Boolean(isFeatureAvailable?.('order'));

        if (orderUuid) {
            navigate(`${jobPagePath(orderUuid, canManageOrders)}${search}`);
            return;
        }

        setOpeningMediaList(true);
        findOrderUuidByJobId(scopedJobId)
            .then(uuid => {
                if (!uuid) {
                    openSnackbar(`Job ${scopedJobId} has no order in the portal, so its media cannot be listed.`, 'warning');
                    return;
                }
                navigate(`${jobPagePath(uuid, canManageOrders)}${search}`);
            })
            .catch(error => {
                console.error("Error opening the filtered media list:", error);
                openSnackbar('Could not open the media list for this job. Please try again.', 'error');
            })
            .finally(() => setOpeningMediaList(false));
    };

    /*
     * Whether the Data Management route would actually render for whoever is looking.
     *
     * App.js answers NotFound to anyone without `restore` or `admin_dashboard`, so a tile that navigated
     * there regardless would take a customer without the restore feature from a page that works to a page
     * that does not. Optional-called for the same reason the order check above is: a card mounted outside
     * AuthProvider's full contract keeps working, and the tile is simply not clickable.
     */
    const canOpenDataManagement = Boolean(isFeatureAvailable?.('restore')) || Boolean(isFeatureAvailable?.('admin_dashboard'));

    /*
     * A reader who cannot open Data Management can still be shown a table the page already has — so the tile
     * remains a control wherever either of the two is true, and falls back to plain text only when neither is.
     */
    /*
     * At all-jobs scope too. These tiles were plain text there because Data Management would have answered
     * an admin with no cid either with a hint or with every company's media; it now scopes itself by the
     * same selector job ids this card counts (see `allJobsScope` in dataManagement.jsx), so the list a
     * click opens holds exactly the media the figure counted. Withheld only when there is nothing
     * onboarded — a figure of zero over no jobs has no rows to list, and the page would be empty.
     */
    const canOpenTapeLibrary = !nothingOnboarded && (canOpenDataManagement || Boolean(onSelectFilter));

    /**
     * Opens the Data Management tape list with the filter that produced a tile's figure.
     *
     * The destination for a tile whose media is not one job's processing run. The three headline tiles —
     * Audit/Catalogued, Physical Stored and Virtualized/Uploaded — describe the account's inventory: what has
     * been catalogued, what is on the shelf, what is in the cloud. Data Management is the page that lists
     * inventory, with the Add and Checkout actions an operator would want next. So those three go there from
     * every page that shows the card, including the processing tab, where In Library and Reading instead
     * narrow the table in place. That split was asked for directly, and it reads consistently: a tile always
     * opens the list its subject belongs to.
     *
     * Unlike openMediaList this does NOT defer to `onSelectFilter`. The tape table on the processing tab is
     * one job's, and narrowing it would answer a different question from the one the tile asks.
     *
     * The filter travels in the query string for the same reasons it does everywhere else — reloadable,
     * bookmarkable, and sendable to a colleague. See mediaFilterNavigation.
     *
     * Deliberately no `openingMediaList` guard and no single-job requirement: there is nothing to look up,
     * so a second click cannot race a request that is in flight.
     *
     * @param {object} filter - A MEDIA_FILTERS descriptor; its `key` is what the page reads back.
     */
    const openTapeLibrary = (filter) => {
        /*
         * Data Management is scoped by the header job selector, and this card is not always. On a job page it
         * is scoped by the order being viewed, which the selector need not be pointing at — that is what the
         * `jobIds` prop exists for. Left alone, a tile counting six tapes for BRO-ING-95402 would open a list
         * of whatever job the selector last held, which is the one failure this whole feature is meant to
         * prevent.
         *
         * So the selector is moved to what the tile counted. A visible side effect, and the right one: after
         * the click the header names the job whose media is listed, so the page and the control that scopes
         * it agree. Skipped when the scope already matches, when the card is following the selector anyway
         * (the dashboard), and when there is no single job and customer to name — a selection holds one job
         * under one customer and cannot express anything else.
         */
        const alignable = jobIds !== undefined && effectiveJobIds.length === 1 && typeof effectiveCid === 'number';
        const alreadyAligned = selectedCid === effectiveCid && selectedJobIds.length === 1 && selectedJobIds[0] === effectiveJobIds[0];
        // Optional-called for the same reason isFeatureAvailable is: a harness mounting this card with a
        // partial context gets a working tile rather than a TypeError on click.
        if (alignable && !alreadyAligned) setSelection?.({[effectiveCid]: [effectiveJobIds[0]]});

        // Whoever the route answers NotFound to keeps the only list they can be shown: the one already on the
        // page, if the page has one. See canOpenTapeLibrary — the tile is plain text when there is neither.
        if (!canOpenDataManagement) {
            onSelectFilter?.(filter);
            return;
        }
        navigate(`${DATA_MANAGEMENT_PATH}${dataManagementFilterSearch(filter.key)}`);
    };

    /*
     * The two tiles about a job's processing run — In Library and Reading — and where they go.
     *
     * Their subject is one job's run, so their first choice is that job's own page, or the table already on
     * screen when the card is mounted above one. Neither exists at all-jobs scope, and the tiles were plain
     * text there; a reader could see six tapes shelved across their clients and had no way to find out which
     * six. Data Management answers that: it takes the same selector job ids this card counts, and both
     * filters are a MEDIA_FILTERS entry it already resolves from the url, so the list holds exactly the media
     * the figure counted. So the destination follows the scope — one job's page when there is one job, the
     * account-wide list when there is not — and the tile is a control at both.
     *
     * Withheld only when neither destination exists: no single job AND nothing for Data Management to list.
     */
    const canOpenJobRunList = canOpenMediaList || canOpenTapeLibrary;
    const openJobRunList = (filter) => (canOpenMediaList ? openMediaList(filter) : openTapeLibrary(filter));

    return (
        /*
         * One provider for the whole card, so every tile inside it draws a skeleton or a figure together.
         * Wrapped at the top rather than around the tile rows because the Refresh button is deliberately
         * outside it: a refresh must never skeleton the figures already on screen — the whole point of the
         * refresh rules below is that it can only improve what is shown — and `loading` is only ever true
         * for the first read, so this stays honest as long as that does.
         */
        <MetricsLoadingContext.Provider value={loading}>
        {/*
         * Fills the height it is given rather than only its own content, so the card lines up along the
         * bottom with the graph beside it. The title row keeps its natural height and the cards below take
         * the rest — with nothing to fill, which is every other mounting of this component, that comes to
         * the same thing as before.
         */}
        <Box sx={{width: '100%', height: '100%', display: 'flex', flexDirection: 'column'}}>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1}}>
                <Box>
                    {/* Matches the widget titles in WidgetsGrid (uiUtils mainHeading) so the sections read as one system. */}
                    <Typography sx={{fontFamily: 'Poppins', fontSize: '16px', fontWeight: 700, lineHeight: '20px', color: '#FFFFFF'}}>
                        Media Metrics
                    </Typography>
                    {/*
                      * Says what the figures cover whenever they cover more than one client.
                      *
                      * Under the title and not in a tooltip, because it changes how every number below it
                      * reads: a reader who assumes they are looking at one customer's inventory has been
                      * misled by the card rather than by their own inattention. This is the whole of what
                      * the old select-a-client prompt was protecting, said in one line instead of by
                      * refusing to answer. See scopeNote for which line.
                      */}
                    {scopeNote && (
                        <Typography sx={{fontFamily: 'Poppins', fontSize: '12px', lineHeight: '16px', color: LABEL_COLOR, pt: 0.25}}>
                            {scopeNote}
                        </Typography>
                    )}
                </Box>
                {/*
                  * Built in the browser from the same API the tiles use, rather than waiting on a
                  * report-export endpoint: the data is a paged read of man_item_list that the portal
                  * already knows how to ask for, and nothing about producing a spreadsheet of it needs
                  * to happen server-side. If a server-rendered report ever arrives this becomes the
                  * fallback for it, not wasted work.
                  *
                  * Withheld at all-jobs scope, where the tiles are now shown but this is not: the report is
                  * a paged read of man_item_list, so there it would be a spreadsheet of every tape of every
                  * customer's every job handed over in one click. Reading a cross-client total is not the
                  * same act as exporting the rows behind it, and only the first of the two was asked for.
                  *
                  * Offered again as soon as jobs are ticked, even when they span clients: the objection was
                  * the size and the blindness of that click, and a reader who has named the jobs has made
                  * neither mistake. The file names itself after them (`media-report-3-jobs-…`), and the sweep
                  * is bounded by the same ids the tiles counted.
                  */}
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                    {/*
                      * Refreshes this card and nothing else.
                      *
                      * The card already re-reads itself every minute, but an operator who has just moved
                      * a tape wants to see it now, and the only control the portal gave them for that was
                      * the browser's reload — which throws away the whole application to re-fetch thirteen
                      * counts, takes the reader back through the session bootstrap, and loses the job
                      * selection and scroll position on the way. This asks the same thirteen questions the
                      * next tick would have asked, and touches nothing else on the page.
                      *
                      * Hidden with the tiles when an admin has picked no client, for the same reason Export
                      * Report is: the query it would run is the one the card refuses to run.
                      *
                      * Left of Export Report because it acts on what is already on screen, where Export
                      * takes the reader out of the page — the more consequential action stays outermost,
                      * where it was, so nobody's muscle memory for it lands on this instead.
                      */}
                    {canRefresh && (
                        /*
                         * describeChild, and a span around the button, for two separate reasons:
                         *
                         *   - without it MUI puts the tooltip on as aria-label, which replaces the accessible
                         *     name rather than adding to it: the button stops being called 'Refresh' to a
                         *     screen reader and starts being called a sentence about page reloads. The word on
                         *     the button is the name; the sentence is a description of it.
                         *   - a disabled button fires no events, so a Tooltip wrapping one directly never
                         *     opens, and MUI warns about it. The span is what stays hoverable while the button
                         *     underneath is disabled — which is exactly when a reader is most likely to hover
                         *     it, wondering why it will not press.
                         */
                        <Tooltip title="Re-read these counts now, without reloading the page" describeChild>
                            <span style={{display: 'inline-flex'}}>
                            <Button
                                variant="contained"
                                onClick={() => refreshNow.current?.()}
                                // Disabled through the first load as well as its own: there is no sense
                                // asking again for figures that have not arrived a first time.
                                disabled={refreshing || loading}
                                startIcon={refreshing
                                    ? <CircularProgress size={14} sx={{color: BUTTON_LABEL_COLOUR}}/>
                                    : <RefreshIcon sx={{fontSize: 18}}/>}
                                sx={{
                                    background: ButtonColorGradient,
                                    color: BUTTON_LABEL_COLOUR,
                                    fontFamily: 'Poppins',
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    borderRadius: 2,
                                    px: 2,
                                    py: 0.5,
                                    boxShadow: 'none',
                                    '&:hover': {background: ButtonColorGradient, boxShadow: 'none'},
                                    /*
                                     * Stated explicitly for the same reason Export Report states it: MUI greys
                                     * a disabled button out, which would leave this one looking pressable
                                     * during the first load and not being. Dimmed instead, as its neighbour is.
                                     */
                                    '&.Mui-disabled': {background: ButtonColorGradient, color: BUTTON_LABEL_COLOUR, opacity: 0.6},
                                }}
                            >
                                {refreshing ? 'Refreshing…' : 'Refresh'}
                            </Button>
                            </span>
                        </Tooltip>
                    )}
                    {canExport && (
                    <Button
                        variant="contained"
                        onClick={handleExport}
                        // Disabled until the scope is known as well as while the sweep runs: at 'All Jobs'
                        // the ids come from the header's list, and a click landing before it does would
                        // sweep unscoped — every company ArkBridge holds, in one file.
                        disabled={exporting || waitingForJobList}
                        startIcon={exporting ? <CircularProgress size={14} sx={{color: BUTTON_LABEL_COLOUR}}/> : null}
                        sx={{
                            background: ButtonColorGradient,
                            color: BUTTON_LABEL_COLOUR,
                            fontFamily: 'Poppins',
                            fontSize: '13px',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: 2,
                            px: 2,
                            py: 0.5,
                            boxShadow: 'none',
                            '&:hover': {background: ButtonColorGradient, boxShadow: 'none'},
                            /*
                             * Stated explicitly because the gradient above would otherwise win: MUI
                             * greys a disabled button with background-color, which a `background`
                             * shorthand overrides, leaving a button that looks pressable and is not.
                             */
                            '&.Mui-disabled': {background: ButtonColorGradient, color: BUTTON_LABEL_COLOUR, opacity: 0.6},
                        }}
                    >
                        {exporting ? 'Exporting…' : 'Export Report'}
                    </Button>
                    )}
                    </Box>
            </Box>

            {/* flexGrow so the two rows of cards share any height left over once the title row has had its
                share, rather than bunching at the top of a taller container. */}
            <Grid container spacing={GRID_GAP} alignItems="stretch" sx={{flexGrow: 1}}>
                {/*
                  * ── Inventory & Processing ────────────────────────────────────────
                  *
                  * Full width now, with Destruction and Shipment moved underneath rather than into a
                  * narrow right-hand column. The card lives in the top-left quarter of the dashboard, so
                  * a side column would have left the nine counters about a third of the page wide; a
                  * single stack of three-across rows keeps every tile the same width as the ones above
                  * it, which is the uniformity asked for, and costs less height than the side-by-side
                  * arrangement did, because that column had to stretch to match this one.
                  */}
                <Grid item xs={12}>
                    <MetricsCard
                        icon={ArchiveIcon}
                        title="Inventory & Processing"
                        action={
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
                                <Typography
                                    sx={{
                                        fontFamily: 'Poppins',
                                        fontSize: '11px',
                                        letterSpacing: '0.08em',
                                        textTransform: 'uppercase',
                                        color: LABEL_COLOR,
                                    }}
                                >
                                    {loading ? 'Loading' : countsUnavailable ? 'Counts Unavailable' : 'Live Status'}
                                </Typography>
                                {/* Indicator sits in place of the dot so the header height never shifts. A
                                    failed read keeps the dot and drops the pulse: the words carry the message,
                                    and a green dot beating over figures nobody could read was the fault. */}
                                {loading ?
                                    <CircularProgress size={8} sx={{color: LABEL_COLOR}}/> :
                                    countsUnavailable ?
                                        <Box sx={{width: 8, height: 8, borderRadius: '50%', bgcolor: 'rgba(238,63,63,1)'}}/> :
                                        <LiveDot/>}
                            </Box>
                        }
                    >
                        <Grid container spacing={GRID_GAP}>
                            <Grid item xs={12} sm={4}>
                                {/*
                                  * Opens the Data Management tape list rather than a job page, from every page
                                  * this card appears on — including the processing tab, whose own table is one
                                  * job's. Catalogued media is the account's inventory, and Data Management is
                                  * where inventory is listed and acted on. The criteria come from the same
                                  * descriptor the count above was built from.
                                  *
                                  * Its hover is that descriptor's copy too, as every filtered tile's is from
                                  * here down — the same words the chip above the table will show, so what the
                                  * tile promises and what the table says it is showing cannot differ. See
                                  * mediaFilterHintText, which also records what the old hints explained and
                                  * where that went: the caveats behind each figure are in the descriptors'
                                  * comments now rather than on the hover.
                                  */}
                                <StatTile
                                    label="Audit/Catalogued"
                                    value={inventory.audited}
                                    progress={inventory.auditedProgress}
                                    onClick={canOpenTapeLibrary ? () => openTapeLibrary(CATALOGUED_FILTER) : undefined}
                                    clickLabel={`Audit/Catalogued: ${formatCount(inventory.audited)}. Show this media in the tape list.`}
                                    hint={mediaFilterHintText(CATALOGUED_FILTER)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                {/*
                                  * To Data Management as well, and for the same reason: media on the shelf is
                                  * the account's inventory rather than one job's run. The list drops destroyed
                                  * cartridges in the browser, which the count does by subtraction — so the
                                  * rows can read short of the total beside them by exactly that many. See
                                  * metadataExcludesInBrowser on the descriptor.
                                  */}
                                <StatTile
                                    label="Physical Stored"
                                    value={inventory.storedOnSite}
                                    caption={inventory.storedOnSiteCaption}
                                    onClick={canOpenTapeLibrary ? () => openTapeLibrary(STORED_FILTER) : undefined}
                                    clickLabel={`Physical Stored: ${formatCount(inventory.storedOnSite)}. Show this media in the tape list.`}
                                    hint={mediaFilterHintText(STORED_FILTER)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                {/*
                                  * To Data Management, completing the headline row: what is in the cloud is
                                  * a fact about the account's inventory, not about one job's run. Unlike
                                  * Physical Stored the whole condition fits in one query, so the rows and
                                  * the total beside them agree exactly — there is no browser-only exclusion
                                  * here. See VIRTUALIZED_FILTER.
                                  */}
                                <StatTile
                                    label="Virtualized/Uploaded"
                                    value={inventory.virtualized}
                                    progress={inventory.virtualizedProgress}
                                    onClick={canOpenTapeLibrary ? () => openTapeLibrary(VIRTUALIZED_FILTER) : undefined}
                                    clickLabel={`Virtualized/Uploaded: ${formatCount(inventory.virtualized)}. Show this media in the tape list.`}
                                    hint={mediaFilterHintText(VIRTUALIZED_FILTER)}
                                />
                            </Grid>
                        </Grid>

                        {/*
                          * ── Virtualization progress ───────────────────────────────
                          *
                          * Three tiles rather than one wide band, mirroring the headline row above at
                          * a slightly smaller size so it reads as subordinate to it. Every figure the
                          * old band showed still has a home: the account total it used to bury inside
                          * 'N of M media virtualized' is now the In Library tile, the in-progress
                          * count is Reading, and the bar plus its caption and Idle/In Progress state
                          * moved into Percentage. Percentage has since been given a question of its
                          * own to answer — how much of the shelved media is outstanding — and its
                          * caption moved onto that sum with it. Overall virtualization coverage is
                          * on the Virtualized/Uploaded bar above.
                          */}
                        {/*
                          * Padded top and bottom but not at the sides, so the three tiles inside sit on the
                          * same columns as the six tiles outside. Padding here made this band a narrower
                          * frame than the card, and thirds of a narrower frame land nowhere near thirds of
                          * the card: Percentage stopped 9px short of Virtualized/Uploaded above it and
                          * Returned below, and In Library hung 3px past Audit/Catalogued. Both come from
                          * this one property. What it costs is 1px of contact at either end, where a tile's
                          * border now meets the band's own. The label keeps its inset of its own, below,
                          * since nothing lines up with it and text against a border reads as a mistake.
                          */}
                        <Box
                            sx={{
                                mt: SECTION_GAP,
                                borderRadius: 2,
                                border: `1px solid ${BORDER}`,
                                background: PROGRESS_BAND_BG,
                                py: 1,
                                px: 0,
                            }}
                        >
                            {/*
                              * Names the group the three tiles belong to, pinned to the band's top-left
                              * corner with the same pulsing dot the card header uses for Live Status —
                              * yellow here, matching the figures inside, so the band reads as the moving
                              * part of the widget.
                              *
                              * component="span" because the dot and the hint icon are elements, and MUI
                              * renders Typography as a <p> by default, which cannot legally contain them.
                              *
                              * justifyContent and textAlign are both stated outright: the dashboard
                              * centres text by default (which is why the tile labels above are centred),
                              * and without them the label floats into the middle of the band.
                              */}
                            <Typography
                                component="span"
                                sx={{
                                    fontFamily: 'Poppins',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                    color: LABEL_COLOR,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-start',
                                    textAlign: 'left',
                                    gap: 0.75,
                                    pb: 0.75,
                                    // The side padding the band gave up, kept here: the tiles below need to
                                    // reach the card's columns, this only needs to stay off the border.
                                    pl: 1,
                                }}
                            >
                                <LiveDot color={YELLOW} size={6}/>
                                Progress
                                <InfoHint title="Live view of media moving through the read and upload pipeline."/>
                            </Typography>
                            <Grid container spacing={GRID_GAP}>
                                <Grid item xs={12} sm={4}>
                                    {/*
                                      * The whole tile opens a tape table filtered to exactly the media
                                      * counted here — the same criteria object drives the count and the
                                      * list, so the two cannot drift apart. Which table depends on how
                                      * many jobs are in scope; see openJobRunList. Reading beside it does
                                      * the same; the pattern is a MEDIA_FILTERS entry and an onClick, so
                                      * any other tile can follow once its criteria are in that table.
                                      */}
                                    <StatTile
                                        size="medium"
                                        background={TILE_BG_SOLID}
                                        label="In Library"
                                        value={virtualization.inLibrary}
                                        onClick={canOpenJobRunList ? () => openJobRunList(IN_LIBRARY_FILTER) : undefined}
                                        clickLabel={`In Library: ${formatCount(virtualization.inLibrary)}. Show this media in the tape list.`}
                                        hint={mediaFilterHintText(IN_LIBRARY_FILTER)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <StatTile
                                        size="medium"
                                        background={TILE_BG_SOLID}
                                        label="Reading"
                                        value={virtualization.inProgressCount}
                                        caption={virtualization.statusLabel}
                                        onClick={canOpenJobRunList ? () => openJobRunList(READING_FILTER) : undefined}
                                        clickLabel={`Reading: ${formatCount(virtualization.inProgressCount)}. Show this media in the tape list.`}
                                        hint={mediaFilterHintText(READING_FILTER)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    {/*
                                      * The one figure on the card that counts down — and, deliberately, the
                                      * one bar that does not follow its figure.
                                      *
                                      * ((base − Done Tapes) / base) × 100 is the share of the tracked media
                                      * still to be finished with, so the number is at its largest when least
                                      * has happened. The bar is drawn as its complement, the share
                                      * concluded, because a bar is read at a glance and the other three on
                                      * this card all fill as things go right: letting this one fill too made
                                      * 100% outstanding look like a job completed. Only the bar was turned
                                      * round — the number, the caption and the hint all still speak in
                                      * outstanding — so it is empty on a shelf where nothing has been done
                                      * and full on one that is clear.
                                      *
                                      * The caption is what keeps that honest. It states the two numbers the
                                      * figure is made from and the word 'outstanding', and it is now the only
                                      * thing on the tile saying that the figure and the bar under it point
                                      * opposite ways.
                                      *
                                      * The base is In Library widened by the damage note, NOT the In Library
                                      * tile two along. Done Tapes is `(location AND status 4/13) OR damaged`
                                      * and a damage note replaces a tape's shelf position rather than
                                      * joining it, so its second branch is disjoint from In Library — the
                                      * division as originally specified subtracted media its denominator
                                      * never held, and read 20% on an account where nothing on the shelf was
                                      * done. Both halves now range over one population. The visible cost is
                                      * that the caption's denominator exceeds the In Library tile by however
                                      * much media is damaged; see percentageBase in mediaMetricsDataRetrieving
                                      * for the measurement.
                                      *
                                      * The hint used to explain that gap, and no longer does — it was cut to
                                      * two sentences on 2026-08-27, the whole tooltip having grown to a
                                      * paragraph nobody reads. What survived is the reading of the figure and
                                      * the one ambiguity in it, that 0% is also what an account with no
                                      * recorded library locations shows. A reader who notices the caption
                                      * disagreeing with the In Library tile is now on their own; this comment
                                      * is the only account of why it does.
                                      *
                                      * The label is still 'Percentage', which names no direction, and is
                                      * what the mockup and the specification both call this tile. Not
                                      * clickable: neither half is a filter a table could be opened on — the
                                      * numerator is a subtraction of two counts, and the base is not a
                                      * single query either now that it spans two notes.
                                      */}
                                    <StatTile
                                        size="medium"
                                        background={TILE_BG_SOLID}
                                        label="Percentage"
                                        value={virtualization.outstandingProgress}
                                        suffix="%"
                                        caption={virtualization.outstandingLabel}
                                        progress={100 - virtualization.outstandingProgress}
                                        hint="Share of the shelved and damaged media still to be finished with. Falls as work concludes, so 0% means everything has been dealt with — or that no library location has been recorded yet."
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* ── Exceptions ──────────────────────────────────────────── */}
                        <Grid container spacing={GRID_GAP} sx={{mt: SECTION_GAP}}>
                            <Grid item xs={12} sm={4}>
                                <StatTile
                                    label="Encrypted"
                                    value={exceptions.encrypted}
                                    hint="Media that could not be read because it is encrypted."
                                    compact
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                {/*
                                  * Both exception tiles open Data Management filtered to the media behind
                                  * the figure, not the job's processing table — the same destination the
                                  * three headline tiles use. A failed or damaged cartridge is something an
                                  * operator acts on next, and the actions are on that page; and the count
                                  * is account-wide rather than one processing run's, so narrowing a single
                                  * job's table in place would answer a different question from the tile's.
                                  *
                                  * The criteria are READ_FAILED_FILTER and DAMAGED_FILTER, which is what
                                  * the counts above read as well, so the number on the tile and the rows it
                                  * opens are the same set and not two similar ones.
                                  */}
                                <StatTile
                                    label="Read Failed"
                                    value={exceptions.readFailed}
                                    onClick={canOpenTapeLibrary ? () => openTapeLibrary(READ_FAILED_FILTER) : undefined}
                                    clickLabel={`Read Failed: ${formatCount(exceptions.readFailed)}. Show this media in the tape list.`}
                                    hint={mediaFilterHintText(READ_FAILED_FILTER)}
                                    tone="error"
                                    compact
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <StatTile
                                    label="Damaged"
                                    value={exceptions.damaged}
                                    onClick={canOpenTapeLibrary ? () => openTapeLibrary(DAMAGED_FILTER) : undefined}
                                    clickLabel={`Damaged: ${formatCount(exceptions.damaged)}. Show this media in the tape list.`}
                                    hint={mediaFilterHintText(DAMAGED_FILTER)}
                                    tone="error"
                                    compact
                                />
                            </Grid>
                        </Grid>
                    </MetricsCard>
                </Grid>

                {/*
                  * ── Destruction + Shipment ────────────────────────────────────────
                  *
                  * Two cards on the row below the Exceptions strip, where they used to be stacked in a
                  * column beside everything above. Side by side they cost one card's height instead of
                  * two.
                  *
                  * Split a third to two thirds rather than down the middle, so each card is as wide as
                  * what it holds: Destruction stacks two full-width rows and needs only enough width for
                  * a label and a number, while Shipment's three tiles sit across and were squeezed into
                  * the same half.
                  *
                  * A third plus CARD_ALIGN_PX, not a bare third, so the right-hand edge of Destruction's
                  * tiles continues the edge of Audit/Catalogued and Encrypted straight down the page — see
                  * that constant for why a third of the row does not land there by itself. Only from `sm`
                  * up: below it the two cards are full width and stacked, with no column to line up with.
                  */}
                <Grid item xs={12} sm={4} sx={(theme) => ({[theme.breakpoints.up('sm')]: THIRD_CARD})}>
                    <MetricsCard icon={DeleteOutlineIcon} title="Destruction">
                        {/*
                          * Bordered tiles, still one figure to a line — the compact StatTile the Exceptions
                          * strip uses, rather than Shipment's icon-above-value tile.
                          *
                          * Which is the shape that fits what these two are. A label beside its number is
                          * what a narrow card can print without abbreviating 'Physical Media', and the
                          * border is what makes a count look clickable: both of these are headed for Data
                          * Management the way the inventory tiles are, and a bare row has nothing to put a
                          * hover state on. The icons go, because this tile has no place for one and the
                          * label was already carrying the meaning.
                          *
                          * Stacked full width rather than side by side: half of a third of the row is too
                          * narrow for a label and a number on one line, and splitting them would trade the
                          * legibility this layout was chosen for.
                          *
                          * `space-between` is what lines the bottom of Virtual Media up with the bottom of
                          * Shipment's tiles beside it. Both cards are already the same height — they share a
                          * Grid row, so the taller one sets it, and that is Shipment — and the two tiles no
                          * longer stretch to fill the difference, now that they are a fixed height shared
                          * with the Exceptions strip. So the spare height goes between them instead of into
                          * them: first tile against the top, second against the bottom, which is the line
                          * Shipment's tiles end on.
                          *
                          * A flex Box rather than a Grid container, which is what the tiles above use. Grid's
                          * spacing is a negative outer margin paid back as padding on the items, so a
                          * stretched container hangs half a gap below the space it was given — invisible while
                          * the container is content-sized, and exactly the few pixels Virtual Media overshot
                          * by once it was told to fill. `gap` spends no margin and so lands on the line.
                          */}
                        {/*
                          * Both to Data Management, which the comment above already promised the borders
                          * were for. Same destination as the inventory tiles and the two exceptions, and
                          * for the same reason: these are account-wide counts of a fact about the media,
                          * with no bearing on any one job's run, and what a reader does next with a
                          * destroyed cartridge — check it off an inventory, produce a certificate — is on
                          * that page.
                          *
                          * The criteria are PHYSICAL_DESTROYED_FILTER and VIRTUAL_DESTROYED_FILTER: one
                          * phrase each, no status, which is what the counts already ask. So unlike
                          * Returned beside them, both figures ARE their own descriptor's count, and the
                          * tile and its table cannot disagree without the request itself being wrong.
                          */}
                        <Box sx={{flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: GRID_GAP}}>
                            <StatTile
                                label="Physical Media"
                                value={destruction.physicalMedia}
                                onClick={canOpenTapeLibrary ? () => openTapeLibrary(PHYSICAL_DESTROYED_FILTER) : undefined}
                                clickLabel={`Physical Media destroyed: ${formatCount(destruction.physicalMedia)}. Show this media in the tape list.`}
                                hint={mediaFilterHintText(PHYSICAL_DESTROYED_FILTER)}
                                compact
                            />
                            <StatTile
                                label="Virtual Media"
                                value={destruction.virtualMedia}
                                onClick={canOpenTapeLibrary ? () => openTapeLibrary(VIRTUAL_DESTROYED_FILTER) : undefined}
                                clickLabel={`Virtual Media destroyed: ${formatCount(destruction.virtualMedia)}. Show this media in the tape list.`}
                                hint={mediaFilterHintText(VIRTUAL_DESTROYED_FILTER)}
                                compact
                            />
                        </Box>
                    </MetricsCard>
                </Grid>

                {/* The complement, so the pair still fills the row exactly and Shipment's right edge stays
                  * under the card above it. */}
                <Grid item xs={12} sm={8} sx={(theme) => ({[theme.breakpoints.up('sm')]: TWO_THIRDS_CARD})}>
                    <MetricsCard icon={LocalShippingIcon} title="Shipment">
                        {/*
                          * Three across on one row, where Returned used to sit on a second row of its own
                          * beneath the two consignment tiles. That second row was what made this card the
                          * tallest thing on the section and so set its height, back when it stretched to
                          * match a neighbouring column — a tile carrying two hard-coded zeros dictating how
                          * much of the screen the counters took.
                          *
                          * Returned keeps its progress bar and so is still the tile with something to say;
                          * it just no longer costs a row to say it.
                          */}
                        <Grid container spacing={GRID_GAP}>
                            <Grid item xs={4}>
                                <ShipmentTile icon={FlightLandIcon} value={shipment.toTapeArk} caption="To TapeArk"/>
                            </Grid>
                            <Grid item xs={4}>
                                <ShipmentTile icon={FlightTakeoffIcon} value={shipment.return} caption="Return"/>
                            </Grid>
                            <Grid item xs={4}>
                                {/*
                                  * The one shipment tile with media behind it, and so the only one that can
                                  * be clicked: the two consignment counters are hard-coded zeros with no
                                  * ArkBridge field to list. It opens Data Management, like the inventory
                                  * tiles above — returned media is the account's, not one processing run's.
                                  *
                                  * Its figure is a subtraction rather than a count of RETURNED_FILTER, which
                                  * is the one place on this card where a tile and its list are the same set
                                  * by algebra instead of by construction. See the descriptor.
                                  */}
                                <ShipmentTile
                                    icon={ReplayIcon}
                                    value={shipment.returned}
                                    caption="Returned"
                                    progress={shipment.returnedProgress}
                                    onClick={canOpenTapeLibrary ? () => openTapeLibrary(RETURNED_FILTER) : undefined}
                                    clickLabel={`Returned: ${formatCount(shipment.returned)}. Show this media in the tape list.`}
                                />
                            </Grid>
                        </Grid>
                    </MetricsCard>
                </Grid>
            </Grid>

            {/* Only ever speaks about the export: a truncated report, an empty one, or a failed one. */}
            <AppSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar(current => ({...current, open: false}))}
            />
        </Box>
        </MetricsLoadingContext.Provider>
    );
};
